import {
  createContext,
  forwardRef,
  useContext,
  useMemo,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import { cn } from "../layout";

export type AgentWorkbenchVariant = "default" | "trend";
export type AgentWorkbenchDensity = "default" | "compact";
export type AgentWorkbenchSurfaceLevel =
  | "page"
  | "panel"
  | "elevated"
  | "subtle"
  | "overlay";

export interface AgentWorkbenchAppearance {
  variant: AgentWorkbenchVariant;
  density: AgentWorkbenchDensity;
}

export interface AgentWorkbenchAppearanceOptions {
  variant?: AgentWorkbenchVariant;
  density?: AgentWorkbenchDensity;
}

const DEFAULT_APPEARANCE: AgentWorkbenchAppearance = {
  variant: "default",
  density: "default",
};

const AgentWorkbenchAppearanceContext = createContext(DEFAULT_APPEARANCE);

export function resolveAgentWorkbenchAppearance(
  inherited: AgentWorkbenchAppearance,
  options: AgentWorkbenchAppearanceOptions = {},
): AgentWorkbenchAppearance {
  const variant = options.variant ?? inherited.variant;
  return {
    variant,
    density:
      options.density ??
      (options.variant === "trend" ? "compact" : inherited.density),
  };
}

export function useAgentWorkbenchAppearance(
  options: AgentWorkbenchAppearanceOptions = {},
): AgentWorkbenchAppearance {
  return resolveAgentWorkbenchAppearance(
    useContext(AgentWorkbenchAppearanceContext),
    options,
  );
}

export interface AgentWorkbenchAppearanceProviderProps
  extends AgentWorkbenchAppearanceOptions {
  children: ReactNode;
}

export function AgentWorkbenchAppearanceProvider({
  children,
  variant,
  density,
}: AgentWorkbenchAppearanceProviderProps) {
  const inherited = useContext(AgentWorkbenchAppearanceContext);
  const appearance = useMemo(
    () => resolveAgentWorkbenchAppearance(inherited, { variant, density }),
    [density, inherited, variant],
  );

  return (
    <AgentWorkbenchAppearanceContext.Provider value={appearance}>
      {children}
    </AgentWorkbenchAppearanceContext.Provider>
  );
}

const SURFACE_CLASSES: Record<AgentWorkbenchSurfaceLevel, string> = {
  page: "bg-background text-foreground",
  panel: "bg-card text-card-foreground",
  elevated: "border border-border bg-popover text-popover-foreground shadow-sm",
  subtle: "bg-muted/40 text-foreground",
  overlay: "border border-border bg-popover text-popover-foreground shadow-lg",
};

export interface AgentWorkbenchSurfaceProps
  extends HTMLAttributes<HTMLDivElement>,
    AgentWorkbenchAppearanceOptions {
  level?: AgentWorkbenchSurfaceLevel;
}

/** Semantic surface primitive shared by Agent workbench regions and overlays. */
export const AgentWorkbenchSurface = forwardRef<
  HTMLDivElement,
  AgentWorkbenchSurfaceProps
>(function AgentWorkbenchSurface(
  { level = "panel", variant, density, className, children, ...props },
  ref,
) {
  const appearance = useAgentWorkbenchAppearance({ variant, density });

  return (
    <div
      {...props}
      ref={ref}
      data-agent-workbench-surface={level}
      data-agent-workbench-variant={appearance.variant}
      data-agent-workbench-density={appearance.density}
      className={cn(SURFACE_CLASSES[level], className)}
    >
      {children}
    </div>
  );
});
